import React, { FunctionComponent, ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Subhead } from '../../atoms/subhead/Subhead'
import { SubheadSize } from '../../styles'
import { ThemeType, tID } from '../../utils'
import { Modal } from '../modal/Modal'

type ModalSize = 'sm' | 'medium' | 'lg' | 'xs' | 'xsmall' | 'small' | 'large'

export interface BaseModalProps {
  /** Function to call when modal is closed */
  closeModal?: () => void
  /** Whether the modal is open */
  isOpen: boolean
  /** Modal body content - can be a string or ReactElement */
  body: string | ReactElement
  /** Optional modal title */
  title?: string
  /** Optional modal footer */
  footer?: string | ReactNode
  /** Whether to show the close button - defaults to true */
  showCloseButton?: boolean
  /** Modal size - maps to width ('sm' = 400px, 'medium' = 544px, 'lg' = 800px) */
  size?: ModalSize
  /** Custom width override - takes precedence over size prop (e.g., '675px') */
  width?: string
  /** Whether clicking outside closes the modal - defaults to true */
  blurable?: boolean
  /** Whether to use fullscreen modal */
  fullScreenModal?: boolean
  /** Adds top padding to prevent content from being covered by header for mobile-sized fullscreen modal */
  enableTopOffset?: boolean
  /** Callback when modal finishes exiting */
  onExited?: () => void
  /** Accessibility label for the modal */
  'aria-label'?: string
  /** Data test id */
  'data-test-id'?: string
}

const ModalContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const TitleContainer = styled.View({
  marginBottom: '8px',
})

const FooterContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  paddingTop: theme.spacing['16px'],
  borderTopWidth: '1px',
  borderTopColor: theme.colors.borderDefault,
}))

const CloseIconContainer = styled.View({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
})

const getWidthFromSize = (size?: ModalSize): string => {
  switch (size) {
    case 'xs':
    case 'xsmall':
    case 'sm':
    case 'small':
      return '400px'
    case 'medium':
      return '544px'
    case 'lg':
    case 'large':
      return '800px'
    default:
      return '544px'
  }
}

/**
 * BaseModal - A cross-platform modal component that provides the same API as the original
 * BaseModal from ui-core. This is a wrapper around the Modal component from ui-core-crossplatform.
 */
export const BaseModal: FunctionComponent<BaseModalProps> = ({
  closeModal = noop,
  isOpen = false,
  body,
  title,
  footer,
  showCloseButton = true,
  size,
  width: customWidth,
  blurable = true,
  fullScreenModal = false,
  enableTopOffset = false,
  onExited,
  'aria-label': ariaLabel,
  'data-test-id': dataTestId,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const handleRequestClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  // For non-fullscreen modals, we render the close icon internally since
  // the underlying Modal's popup mode doesn't include a header with close icon
  const shouldShowInternalCloseIcon = showCloseButton && !fullScreenModal

  const modalContents = useMemo(() => {
    const bodyContent =
      typeof body === 'string' ? (
        <BodyText size={BodyTextSize.DEFAULT} text={body} />
      ) : React.isValidElement(body) ? (
        React.cloneElement(body as ReactElement, { closeModal })
      ) : (
        body // fallback for unexpected types
      )
    return (
      <ModalContentContainer testID={tID(dataTestId || 'BaseModal-content')}>
        {shouldShowInternalCloseIcon && (
          <CloseIconContainer testID={tID('Modal-XIcon')}>
            <PressableOpacity
              onPress={closeModal}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close',
                description: 'Accessibility label for close button',
              })}
            >
              <XIcon size={24} fillColor={colors.iconDefault} />
            </PressableOpacity>
          </CloseIconContainer>
        )}
        {title && (
          <TitleContainer>
            <Subhead size={SubheadSize.MEDIUM} text={title} color={colors.textPrimary} />
          </TitleContainer>
        )}
        {bodyContent}
        {footer && (
          <FooterContainer>
            {typeof footer === 'string' ? <BodyText size={BodyTextSize.DEFAULT} text={footer} /> : footer}
          </FooterContainer>
        )}
      </ModalContentContainer>
    )
  }, [
    body,
    closeModal,
    colors.iconDefault,
    colors.textPrimary,
    dataTestId,
    footer,
    formatMessage,
    shouldShowInternalCloseIcon,
    title,
  ])

  const width = customWidth || getWidthFromSize(size)

  return (
    <Modal
      visible={isOpen}
      onRequestClose={handleRequestClose}
      onCloseEnd={onExited}
      handleAnimationEnd={onExited}
      modalContents={modalContents}
      width={width}
      showCloseIcon={fullScreenModal && showCloseButton}
      closeOnScrim={blurable}
      disableBottomSheet
      disableFullScreenModal={!fullScreenModal}
      enableTopOffset={enableTopOffset}
      accessibilityLabel={
        ariaLabel ||
        formatMessage({
          defaultMessage: 'Modal dialog',
          description: 'Default accessibility label for modal',
        })
      }
    />
  )
}

export default BaseModal
